import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { ArrowRight, ChevronLeft, ChevronRight, Heart, Quote, Sparkles, Star } from 'lucide-react';
import { useFailedImages } from '../../hooks/useFailedImages';
import './SuccessStoriesSection.css';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const imgUrl = path => (path ? `${API}${path}` : '');

const storyMeta = story => [
    story.religion,
    story.city,
    story.marriageDate && `Married ${new Date(story.marriageDate).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })}`,
].filter(Boolean).join(' · ');

const initial = name => (name || '').trim().charAt(0).toUpperCase() || '♥';

// Heading and previous / next arrows on the left, one story card (couple photo + quote) on the right.
const SuccessStoriesSection = () => {
    const [stories, setStories] = useState([]);
    const [active, setActive] = useState(0);
    const [loading, setLoading] = useState(true);
    const [failedImages, markImageFailed] = useFailedImages();

    useEffect(() => {
        axios.get(`${API}/api/testimonials`)
            .then(response => setStories((response.data.testimonials || []).slice(0, 12)))
            .catch(() => setStories([]))
            .finally(() => setLoading(false));
    }, []);

    const count = stories.length;
    const index = count ? active % count : 0;
    const story = count ? stories[index] : null;
    const go = step => setActive(current => (current + step + count) % count);
    const hasPhoto = story?.couplePhoto && !failedImages.has(story.couplePhoto);

    return (
        <section className="ss" aria-labelledby="ss-title">
            <div className="ss__inner">
                <div className="ss__intro">
                    <span className="ss__kicker"><Heart size={13} fill="currentColor" /> Real couples · Real stories</span>
                    <h2 id="ss-title">Where Two Hearts Become One <em>Beautiful Story</em></h2>
                    <p>Every story is a reminder that the right beginning can change everything.</p>

                </div>

                {loading ? (
                    <article className="ss-story ss-story--loading" aria-label="Loading success stories" aria-busy="true">
                        <div className="ss-story__photo" />
                        <div className="ss-story__quote"><span /><span /><span /></div>
                    </article>
                ) : story ? (
                    <article className="ss-story" key={story._id} aria-live="polite">
                        <div className="ss-story__photo">
                            {hasPhoto ? (
                                <img
                                    src={imgUrl(story.couplePhoto)}
                                    alt={`${story.groomName} and ${story.brideName}`}
                                    loading="lazy"
                                    onError={() => markImageFailed(story.couplePhoto)}
                                />
                            ) : (
                                <div className="ss-story__monogram" aria-hidden="true">
                                    <div className="ss-story__initials">
                                        <span>{initial(story.groomName)}</span>
                                        <Heart size={26} fill="currentColor" />
                                        <span>{initial(story.brideName)}</span>
                                    </div>
                                    <strong>Happily Married</strong>
                                </div>
                            )}
                            {hasPhoto && <span className="ss-story__badge"><Heart size={13} fill="currentColor" /> Happily Married</span>}
                        </div>
                        <div className="ss-story__quote">
                            <div className="ss-story__top">
                                <Quote className="ss-story__mark" size={44} fill="currentColor" />
                                <span className="ss-story__stars" role="img" aria-label="5 out of 5 stars">{[0, 1, 2, 3, 4].map(i => <Star key={i} size={18} fill="currentColor" />)}</span>
                            </div>
                            {story.message && <blockquote>{story.message}</blockquote>}
                            <div className="ss-story__couple">
                                <h3>{story.groomName} <Heart size={14} fill="currentColor" /> {story.brideName}</h3>
                                <small>{storyMeta(story)}</small>
                            </div>
                            {count > 1 && (
                                <div className="ss-story__dots" role="tablist" aria-label="Choose a story">
                                    {stories.map((item, i) => (
                                        <button type="button" role="tab" key={item._id} aria-selected={i === index} aria-label={`Story ${i + 1}`} className={i === index ? 'is-active' : ''} onClick={() => setActive(i)} />
                                    ))}
                                </div>
                            )}
                        </div>
                    </article>
                ) : (
                    <article className="ss-story ss-story--empty">
                        <span className="ss-story--empty__icon"><Sparkles size={32} /></span>
                        <p>Beautiful beginnings are being written.</p>
                        <h3>Our next success story could be yours.</h3>
                        <span>Explore verified matches or share your journey with the Gettimelam community.</span>
                        <Link to="/success-stories">Explore success stories <ArrowRight size={15} /></Link>
                    </article>
                )}

                <div className="ss__actions">
                {count > 1 && (
                    <div className="ss__nav">
                        <button type="button" onClick={() => go(-1)} aria-label="Previous story"><ChevronLeft size={20} /></button>
                        <button type="button" onClick={() => go(1)} aria-label="Next story"><ChevronRight size={20} /></button>
                    </div>
                )}
                <Link className="ss__more" to="/success-stories">Read more love stories <ArrowRight size={16} /></Link>
                </div>
            </div>
        </section>
    );
};

export default SuccessStoriesSection;
